import { useState, useMemo, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, ShieldCheck, Eye } from 'lucide-react';
import api from '../../lib/api';
import { useTransactions, useTransactionSummary, useOverrideTransaction, useTransactionRawText, useHiddenAssetSignals } from '../../hooks/useTransactions';
import type { Transaction } from '../../types';
import { formatTxnDate } from '../../utils/dates';
import { formatAccountName } from '../../utils/accounts';
import {
  Button,
  Card,
  EmptyState,
  Modal,
  Money,
  Select,
  SkeletonTable,
  Tabs,
  Tag,
  TextArea,
} from '../ui';
import TransactionTable from '../TransactionTable';
import SourcePageViewer from '../SourcePageViewer';
import WasteClaimsView from './WasteClaimsView';
import AssetSignalsView from './AssetSignalsView';
import { TXN_OVERRIDE_OPTIONS } from '../../lib/enums';

type SubView = 'all' | 'waste' | 'review' | 'signals';

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

let toastIdCounter = 0;

interface TransactionsTabProps {
  caseId: string;
  defaultBankLabel?: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
  subView?: SubView;
  separationDate?: string | null;
}

export default function TransactionsTab({ caseId, defaultBankLabel, clientName, opposingPartyName, subView: controlledSubView, separationDate }: TransactionsTabProps) {
  const [exporting, setExporting] = useState(false);
  const [internalSubView, setInternalSubView] = useState<SubView>('all');
  const subView = controlledSubView ?? internalSubView;
  const setSubView = setInternalSubView;
  const subNavControlled = controlledSubView !== undefined;
  const [accountFilters, setAccountFilters] = useState<string[]>(defaultBankLabel ? [defaultBankLabel] : []);
  const [partyFilter, setPartyFilter] = useState<'client' | 'opposing' | 'third_party' | 'joint' | ''>('');
  const [afterSeparation, setAfterSeparation] = useState(false);
  const [includeWorkProduct, setIncludeWorkProduct] = useState(false);

  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const toggleAccount = useCallback((label: string) => {
    setAccountFilters((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  }, []);
  const clearAccounts = useCallback(() => setAccountFilters([]), []);

  const handleExportXlsx = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params: Record<string, string | string[]> = {};
      if (accountFilters.length > 0) params.accounts = accountFilters;
      if (partyFilter) params.party = partyFilter;
      if (afterSeparation && separationDate) params.after_date = separationDate;
      if (includeWorkProduct) params.include_work_product = 'true';
      const res = await api.get(`/cases/${caseId}/transactions.xlsx`, {
        responseType: 'blob',
        params,
        paramsSerializer: { indexes: null },
      });
      const cd = (res.headers?.['content-disposition'] || '') as string;
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `shale_case_${caseId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('XLSX export failed', err);
      addToast('error', 'Sorry — the Excel export failed. Check the console for details.');
    } finally {
      setExporting(false);
    }
  }, [caseId, exporting, accountFilters, partyFilter, afterSeparation, separationDate, includeWorkProduct, addToast]);
  const { data: transactions = [], isLoading, isError, error } = useTransactions(caseId);
  const { data: summary } = useTransactionSummary(caseId);
  const { data: signalData } = useHiddenAssetSignals(caseId);

  useEffect(() => {
    setAccountFilters(defaultBankLabel ? [defaultBankLabel] : []);
  }, [defaultBankLabel]);

  const [overrideModal, setOverrideModal] = useState<Transaction | null>(null);
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideSuccess, setOverrideSuccess] = useState(false);
  const overrideMutation = useOverrideTransaction();

  const bankLabelOptions = useMemo(() => {
    const labels = Array.from(new Set(transactions.map((t) => t.bank_label).filter(Boolean))) as string[];
    return labels.sort();
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    let result = transactions;
    if (accountFilters.length > 0) result = result.filter((t) => t.bank_label && accountFilters.includes(t.bank_label));
    if (partyFilter) result = result.filter((t) => t.source_party === partyFilter);
    if (afterSeparation && separationDate) {
      result = result.filter((t) => t.date && t.date >= separationDate);
    }
    return result;
  }, [transactions, accountFilters, partyFilter, afterSeparation, separationDate]);

  const wasteTransactions = useMemo(
    () => filteredTransactions.filter((t) => t.waste_flag),
    [filteredTransactions]
  );

  const needsReviewTransactions = useMemo(
    () => filteredTransactions.filter((t) => (t.confidence_score || 0) < 50),
    [filteredTransactions]
  );

  const totalWasteAmount = useMemo(
    () => wasteTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0),
    [wasteTransactions]
  );

  const signalCount = useMemo(() => {
    if (accountFilters.length === 0) return signalData?.total_flagged ?? 0;
    const byCat = signalData?.by_category ?? {};
    return Object.values(byCat)
      .flat()
      .filter((t) => t.bank_label && accountFilters.includes(t.bank_label)).length;
  }, [signalData, accountFilters]);

  const handleOpenOverride = (txn: Transaction) => {
    setOverrideModal(txn);
    setOverrideStatus(txn.property_status);
    setOverrideReason('');
    setOverrideSuccess(false);
  };

  const handleOverrideSubmit = () => {
    if (!overrideModal || !overrideReason.trim()) return;
    overrideMutation.mutate(
      { transactionId: overrideModal.id, override: { property_status: overrideStatus, override_reason: overrideReason } },
      {
        onSuccess: () => {
          setOverrideSuccess(true);
          addToast('success', 'Override saved successfully');
          setTimeout(() => {
            setOverrideModal(null);
            setOverrideSuccess(false);
          }, 1500);
        },
        onError: (err: Error) => {
          addToast('error', `Override failed: ${err.message || 'Unknown error'}`);
        },
      }
    );
  };

  if (isLoading) {
    return <SkeletonTable rows={10} columns={6} />;
  }

  if (isError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center space-y-4">
        <div className="rounded-full bg-danger-soft p-3">
          <XCircle size={24} className="text-danger" />
        </div>
        <div className="text-center">
          <p className="t-card text-danger">Failed to load transactions</p>
          <p className="mt-1 text-sm text-ink-faint">
            {(error as Error)?.message || 'An unexpected error occurred. Please try again.'}
          </p>
        </div>
      </div>
    );
  }

  const subViews: { id: SubView; label: string; count: number; amber?: boolean }[] = [
    { id: 'all', label: 'All Transactions', count: filteredTransactions.length },
    { id: 'waste', label: 'Potential Waste', count: wasteTransactions.length },
    { id: 'review', label: 'Needs Review', count: needsReviewTransactions.length },
    { id: 'signals', label: 'Asset Signals', count: signalCount, amber: true },
  ];

  return (
    <div className="space-y-6 relative">
      <div className="pointer-events-none fixed right-4 bottom-4 z-(--z-toast) flex w-80 flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.type === 'error' ? 'alert' : 'status'}
            className="pointer-events-auto flex items-start gap-3 rounded-overlay border border-border bg-surface-card p-4 shadow-overlay animate-toast"
          >
            {toast.type === 'success' ? (
              <CheckCircle size={16} className="mt-(--space-hair) shrink-0 text-good" />
            ) : (
              <XCircle size={16} className="mt-(--space-hair) shrink-0 text-danger" />
            )}
            <p className="t-label min-w-0 flex-1 text-ink">{toast.message}</p>
          </div>
        ))}
      </div>

      {!subNavControlled && (
        <Tabs
          items={subViews.map((sv) => ({
            id: sv.id,
            label: sv.label,
            count: sv.count,
            amber: sv.amber && sv.count > 0,
          }))}
          value={subView}
          onChange={(id) => setSubView(id as SubView)}
          level="secondary"
          ariaLabel="Transaction views"
        />
      )}

      {subView === 'all' && (
        <AllTransactionsView
          transactions={filteredTransactions}
          summary={accountFilters.length > 0 ? null : summary}
          onOverride={handleOpenOverride}
          caseId={caseId}
          clientName={clientName}
          opposingPartyName={opposingPartyName}
          totalCount={transactions.length}
          accountOptions={bankLabelOptions}
          accountFilters={accountFilters}
          onToggleAccount={toggleAccount}
          onClearAccounts={clearAccounts}
          partyFilter={partyFilter}
          onPartyChange={setPartyFilter}
          afterSeparation={afterSeparation}
          onAfterSeparationChange={setAfterSeparation}
          separationDate={separationDate}
          onExport={handleExportXlsx}
          exporting={exporting}
          includeWorkProduct={includeWorkProduct}
          onToggleWorkProduct={setIncludeWorkProduct}
        />
      )}

      {subView === 'waste' && (
        <WasteClaimsView
          caseId={caseId}
          transactions={filteredTransactions}
          wasteTransactions={wasteTransactions}
          totalWasteAmount={totalWasteAmount}
          onOverride={handleOpenOverride}
        />
      )}

      {subView === 'review' && (
        <NeedsReviewView transactions={needsReviewTransactions} totalTransactions={filteredTransactions.length} onOverride={handleOpenOverride} />
      )}

      {subView === 'signals' && (
        <AssetSignalsView caseId={caseId} accountFilters={accountFilters} />
      )}

      <Modal
        open={!!overrideModal}
        onClose={() => { setOverrideModal(null); setOverrideSuccess(false); }}
        title="Override Classification"
        footer={
          overrideSuccess ? undefined : (
            <>
              <Button variant="secondary" onClick={() => setOverrideModal(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleOverrideSubmit}
                loading={overrideMutation.isPending}
                disabled={!overrideReason.trim()}
              >
                Apply Override
              </Button>
            </>
          )
        }
      >
        {overrideModal && !overrideSuccess && (
          <div className="space-y-4">
            <div className="rounded-lg bg-surface-well p-3">
              <p className="text-sm font-medium text-ink">{overrideModal.description}</p>
              <p className="mt-1 text-xs text-ink-faint">
                <Money value={Math.abs(overrideModal.amount)} /> — {formatTxnDate(overrideModal.date)}
              </p>
            </div>
            <Select
              label="New Property Status"
              value={overrideStatus}
              onChange={(e) => setOverrideStatus(e.target.value)}
              options={TXN_OVERRIDE_OPTIONS}
            />
            <TextArea
              label="Reason for Override (required)"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Explain why this classification is being changed..."
            />
            {overrideMutation.isError && (
              <div className="flex items-center gap-2 rounded-lg border border-danger bg-danger-soft p-3 text-sm text-danger">
                <XCircle size={16} className="shrink-0" />
                {(overrideMutation.error as Error)?.message || 'Failed to apply override'}
              </div>
            )}
          </div>
        )}
        {overrideSuccess && (
          <div className="flex flex-col items-center justify-center space-y-3 py-6">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle size={24} className="text-green-700" />
            </div>
            <p className="t-card text-green-900">Override Applied</p>
            <p className="text-sm text-ink-faint">The classification has been updated.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function AllTransactionsView({
  transactions,
  onOverride,
  caseId,
  clientName,
  opposingPartyName,
  totalCount,
  accountOptions,
  accountFilters,
  onToggleAccount,
  onClearAccounts,
  partyFilter,
  onPartyChange,
  afterSeparation,
  onAfterSeparationChange,
  separationDate,
  onExport,
  exporting,
  includeWorkProduct,
  onToggleWorkProduct,
}: {
  transactions: Transaction[];
  /** Case-level summary; currently accepted but unused by the rebuilt table. */
  summary: unknown;
  onOverride: (t: Transaction) => void;
  caseId: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
  totalCount: number;
  accountOptions: string[];
  accountFilters: string[];
  onToggleAccount: (label: string) => void;
  onClearAccounts: () => void;
  partyFilter: 'client' | 'opposing' | 'third_party' | 'joint' | '';
  onPartyChange: (p: 'client' | 'opposing' | 'third_party' | 'joint' | '') => void;
  afterSeparation: boolean;
  onAfterSeparationChange: (v: boolean) => void;
  separationDate?: string | null;
  onExport: () => void;
  exporting: boolean;
  includeWorkProduct: boolean;
  onToggleWorkProduct: (v: boolean) => void;
}) {
  return (
    <div className="space-y-6">
      <TransactionTable
        transactions={transactions}
        onOverride={onOverride}
        caseId={caseId}
        clientName={clientName}
        opposingPartyName={opposingPartyName}
        totalCount={totalCount}
        accountOptions={accountOptions}
        accountFilters={accountFilters}
        onToggleAccount={onToggleAccount}
        onClearAccounts={onClearAccounts}
        partyFilter={partyFilter}
        onPartyChange={onPartyChange}
        afterSeparation={afterSeparation}
        onAfterSeparationChange={onAfterSeparationChange}
        separationDate={separationDate}
        onExport={onExport}
        exporting={exporting}
        includeWorkProduct={includeWorkProduct}
        onToggleWorkProduct={onToggleWorkProduct}
      />
    </div>
  );
}

function SourceLink({ txn }: { txn: Transaction }) {
  const [open, setOpen] = useState(false);
  const canView = txn.statement_id && txn.source_page !== null;

  if (!txn.source_file) return null;

  if (!canView) {
    return <span>{txn.source_file}</span>;
  }

  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- inline citation link: no ui/LinkButton primitive exists yet */}
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 text-green-700 transition-colors hover:text-green-900 hover:underline"
      >
        <Eye size={12} />
        {txn.source_file} (p.{txn.source_page})
      </button>
      <SourcePageViewer
        statementId={txn.statement_id}
        pageNumber={txn.source_page!}
        sourceFile={txn.source_file}
        isOpen={open}
        onClose={() => setOpen(false)}
        highlight={txn.location}
      />
    </>
  );
}


function NeedsReviewCard({ txn, onOverride }: { txn: Transaction; onOverride: (t: Transaction) => void }) {
  const { data: rawTextData } = useTransactionRawText(txn.id, true);
  const rawText = rawTextData?.raw_text ?? null;

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium text-ink">{txn.description}</p>
              <Tag size="sm" tone={txn.confidence_score !== null && txn.confidence_score < 25 ? 'danger' : 'warn'}>
                {txn.confidence_score !== null ? `${txn.confidence_score}%` : 'N/A'}
              </Tag>
            </div>
            <div className="mt-1 flex gap-4 text-xs text-ink-faint">
              <span>{formatTxnDate(txn.date)}</span>
              <Money value={txn.amount} tone="auto" />
              <span>{formatAccountName(txn.bank_label)}</span>
              <SourceLink txn={txn} />
            </div>
          </div>
          <Button variant="secondary" size="sm" className="ml-3" onClick={() => onOverride(txn)}>
            Classify
          </Button>
        </div>

        {rawText && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <p className="t-eyebrow mb-1">AI-Read Description</p>
              <p className="rounded-control border border-border-subtle bg-surface-page p-2 text-xs text-ink">
                {txn.description}
              </p>
            </div>
            <div>
              <p className="t-eyebrow mb-1">Original Statement Text</p>
              <p className="rounded-control border border-border-subtle bg-surface-page p-2 font-mono text-xs text-ink-soft">
                {rawText}
              </p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function NeedsReviewView({
  transactions,
  totalTransactions,
  onOverride,
}: {
  transactions: Transaction[];
  totalTransactions: number;
  onOverride: (t: Transaction) => void;
}) {
  if (transactions.length === 0) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="All transactions are high-confidence"
        description={`All ${totalTransactions} transactions have confidence scores of 50% or above. No manual review needed.`}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-faint">
          These transactions have low confidence scores (&lt;50%) and may need manual review.
        </p>
        <Tag tone="warn">{transactions.length} of {totalTransactions} transactions</Tag>
      </div>
      <div className="space-y-3">
        {transactions.map((txn) => (
          <NeedsReviewCard key={txn.id} txn={txn} onOverride={onOverride} />
        ))}
      </div>
    </div>
  );
}
